/** Shared clipboard-write state for copyable dashboard values and licence blobs. */
import { useState } from 'react';

export interface UseClipboardCopyResult {
  /** True once `copy` has genuinely written to the clipboard. Reset only by
   *  a fresh successful copy (callers that need a per-render reset should
   *  key the component, same as before this hook existed). */
  copied: boolean;
  /** Writes `text` to the clipboard when the API is available, resolving to
   *  whether it actually succeeded. Callers that offer a manual-select
   *  fallback (e.g. a read-only text field) should trigger it themselves
   *  when this resolves false. */
  copy: (text: string) => Promise<boolean>;
}

export function useClipboardCopy(): UseClipboardCopyResult {
  const [copied, setCopied] = useState(false);

  const copy = async (text: string): Promise<boolean> => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        return true;
      }
    } catch {
      // clipboard unavailable/denied -- caller may fall back to manual select
    }
    return false;
  };

  return { copied, copy };
}

export default useClipboardCopy;
